/**
 * A case's page (case platform): where the job stands — its stages, each read from the case database or the
 * desk, never typed — its deliverables and their sign-off, the files it received, and its people. The desk
 * (the solution's app, opened on this case) and the case's canvas (its deliverables and its chat) are one
 * click away.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useParams } from "@tanstack/react-router";
import { ArrowLeft, ExternalLink, MessageSquare, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { Skeleton } from "@/components/ui/skeleton";

import { useApi } from "@/api";
import { canEditCase, canManageCase, deskHref, getCase, refreshCaseProgress } from "@/api/cases";
import { getProject } from "@/api/plan";
import {
  CaseStatusPill,
  DeliverablesSection,
  FilesSection,
  PeopleList,
  StageList,
  when,
} from "@/components/case/CaseParts";
import { DeskScreen } from "@/components/plan/DeskScreen";
import { SpaceMembersButton } from "@/components/plan/SpaceMembersButton";
import { useDocumentTitle } from "@/lib/document-title";

export const Route = createFileRoute("/_app/w/$slug/cases/$caseId")({
  component: CasePage,
});

function CasePage() {
  const { slug, caseId } = useParams({ from: "/_app/w/$slug/cases/$caseId" });
  const api = useApi();
  const qc = useQueryClient();
  const detail = useQuery({ queryKey: ["case", caseId], queryFn: () => getCase(caseId) });
  const workspaces = useQuery({ queryKey: ["workspaces"], queryFn: () => api.listWorkspaces() });
  const workspace = workspaces.data?.find((w) => w.slug === slug);
  const projectId = detail.data?.project_id ?? null;
  const project = useQuery({
    queryKey: ["plan", "project", projectId],
    queryFn: () => getProject(projectId!),
    enabled: !!projectId,
    retry: false,
  });
  const reread = useMutation({
    mutationFn: () => refreshCaseProgress(caseId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["case", caseId] });
      void qc.invalidateQueries({ queryKey: ["case-deliverables", caseId] });
      void qc.invalidateQueries({ queryKey: ["case-intake", caseId] });
      toast.success("Read again");
    },
    onError: (e) => toast.error((e as Error).message),
  });

  useDocumentTitle([detail.data?.name ?? "Case", workspace?.name]);

  if (detail.isLoading) {
    return (
      <DeskScreen>
        <Skeleton className="h-8 w-72" />
        <Skeleton className="mt-4 h-40 w-full" />
      </DeskScreen>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <DeskScreen>
        <Link to="/w/$slug" params={{ slug }} className="back">
          <ArrowLeft size={14} /> Back to the workspace
        </Link>
        <p className="mt-4 text-[13px] text-[var(--color-semantic-error)]">
          {(detail.error as Error)?.message ?? "This case is not here."}
        </p>
      </DeskScreen>
    );
  }

  const c = detail.data;
  const mayEdit = canEditCase(c.my_role);
  const desk = deskHref(c);
  const current = c.stages.find((s) => s.key === c.stage);

  const toolbar = (
    <div className="desk-head">
      <Link to="/w/$slug" params={{ slug }} className="back">
        <ArrowLeft size={14} /> {workspace?.name ?? "Workspace"}
      </Link>
      <div className="crumb">
        CASE
        {c.reference && (
          <>
            {" "}
            <span className="sep">·</span> {c.reference}
          </>
        )}{" "}
        <span className="sep">·</span> {c.solution_id}
        {c.solution_version ? ` ${c.solution_version}` : ""}
      </div>
      <div className="obj-head" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <h1 className="page" style={{ margin: 0 }}>
          {c.name}
        </h1>
        <div className="case-actions">
          {desk && (
            <a className="btn primary" href={desk} target="_blank" rel="noopener noreferrer">
              Open the desk <ExternalLink size={13} />
            </a>
          )}
          {c.canvas_id && (
            <Link className="btn" to="/w/$slug/c/$canvasId" params={{ slug, canvasId: c.canvas_id }}>
              <MessageSquare size={13} /> Canvas and chat
            </Link>
          )}
          {canManageCase(c.my_role) && <SpaceMembersButton spaceId={c.id} subjectLabel="case" />}
        </div>
      </div>
      {c.description && <p className="page-sub">{c.description}</p>}
      <div className="obj-summary">
        <span className="obj-sum-pct">{c.progress}% done</span>
        <span className="obj-sum-dot" />
        <span className="obj-sum-counts">
          {c.stage === "done" ? "Every stage done" : current ? `At ${current.title ?? current.key}` : "Opened"}
          {c.created_at ? ` · opened ${when(c.created_at)}` : ""}
        </span>
        <CaseStatusPill status={c.status} />
        {mayEdit && (
          <button
            type="button"
            className="btn ghost sm"
            onClick={() => reread.mutate()}
            disabled={reread.isPending}
            title="Read where the case stands again, from the case database and the desk"
          >
            <RefreshCw size={12} className={reread.isPending ? "animate-spin motion-reduce:animate-none" : undefined} />
            {reread.isPending ? "Reading…" : "Read again"}
          </button>
        )}
      </div>
    </div>
  );

  const footer = (
    <div className="section">
      <h2 className="sec" style={{ margin: "0 0 4px" }}>
        People on this case
      </h2>
      <PeopleList caseId={c.id} />
      {project.data && (
        <p className="sec-note" style={{ marginTop: 14 }}>
          Its stages are also the work packages of{" "}
          <Link className="case-link" to="/plan/p/$projectSlug" params={{ projectSlug: project.data.project.slug }}>
            the {project.data.project.name} project
          </Link>
          , so its progress shows in Plan.
        </p>
      )}
    </div>
  );

  return (
    <DeskScreen toolbar={toolbar} footer={footer}>
      <section className="case-section" aria-labelledby="case-stages-h">
        <div className="kr-head-row" style={{ marginTop: 0 }}>
          <h2 className="sec" id="case-stages-h" style={{ margin: 0 }}>
            Where it stands
          </h2>
        </div>
        <StageList stages={c.stages} current={c.stage} />
      </section>
      <DeliverablesSection caseId={c.id} slug={slug} canvasId={c.canvas_id} mayEdit={mayEdit} />
      <FilesSection caseId={c.id} />
    </DeskScreen>
  );
}
